import type { ISkuGroup, ISkuSelectorOption, TSkuSelectorProduct } from '../models';
import type { IProductEntityCharacteristic } from '~/entities';

import { CharacteristicsKeyGenerator } from './CharacteristicsKeyGenerator';

export class CharacteristicsController {
  public skuGroups: ISkuGroup[] = [];
  public skuGroupsIds: number[];
  public optionsTitles = new Map<number, string>();

  private alwaysUnavailableOptionIds = new Set<number>();
  private characteristicsMap = new Map<string, TSkuSelectorProduct>();
  private unavailableKeys = new Set<string>();

  constructor(products: TSkuSelectorProduct[], characteristics: IProductEntityCharacteristic[]) {
    this.calcSkuGroups(characteristics);
    this.calcCharacteristicsMap(products);
  }

  public getSelectedKey(): string {
    return CharacteristicsKeyGenerator.byIds(this.getSelectedIds());
  }

  private getSelectedIds(): number[] {
    return this.skuGroups.map((it) => it.selectedOptionId.value);
  }

  public getSelectedProduct() {
    return this.characteristicsMap.get(this.getSelectedKey());
  }

  public setSelectionByProduct({ characteristicValues }: TSkuSelectorProduct) {
    this.skuGroups.forEach((skuGroup) => {
      const selectedId = characteristicValues.find((it) => it.characteristicId === skuGroup.id)?.id;
      skuGroup.selectedOptionId.value = selectedId;
    });
  }

  private calcSkuGroups(characteristics: IProductEntityCharacteristic[]) {
    characteristics.forEach((char, index) => {
      this.skuGroups.push({
        id: char.id,
        options: char.values.map((it) => ({ ...it, order: index })),
        title: char.title,
        selectedOptionId: ref(),
        type: char.type,
      });

      char.values.forEach(({ id, title }) => {
        this.alwaysUnavailableOptionIds.add(id);
        this.optionsTitles.set(id, title);
      });
    });

    this.skuGroupsIds = this.skuGroups.map((it) => it.id);
  }

  private calcCharacteristicsMap(products: TSkuSelectorProduct[]) {
    products.forEach((product) => {
      const key = CharacteristicsKeyGenerator.byProduct(product, this.skuGroupsIds);
      this.characteristicsMap.set(key, product);

      if (product.availableAmount === 0) {
        this.unavailableKeys.add(key);
        return;
      }

      product.characteristicValues.forEach(({ id }) => this.alwaysUnavailableOptionIds.delete(id));
    });
  }

  public isUnavailableOption(option: ISkuSelectorOption) {
    if (this.alwaysUnavailableOptionIds.has(option.id)) return true;

    const selectedIds = this.getSelectedIds();
    if (selectedIds.length < this.skuGroupsIds.length - 1) return false;

    const idsForKey = [...selectedIds];
    idsForKey[option.order] = option.id;

    return this.unavailableKeys.has(CharacteristicsKeyGenerator.byIds(idsForKey));
  }
}
